import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Filter } from "@/components/Icon";
import { getCategoryBySlug, productCategories } from "@/data/productCatalog";
import { useEffect, useState, useMemo } from "react";
import { trackEvent } from "@/lib/analytics";
import { useToptexProducts, imageSrc, imageSrcSet, imageFallback } from "@/hooks/useToptex";

// Vertaalt de redactionele categorie-/subcategorie-slugs naar de live catalogusfilters.
const GENDER_BY_SLUG: Record<string, string> = {
  heren: "Heren",
  dames: "Dames",
};
const CATEGORY_BY_SLUG: Record<string, string> = {
  "kids-baby": "kids",
  sport: "sport",
  workwear: "workwear",
  accessoires: "accessoires",
  tassen: "bag",
};
const CATEGORY_BY_SUBSLUG: Record<string, string> = {
  "t-shirts": "tshirt",
  polos: "polo",
  hoodies: "sweater",
  sweaters: "sweater",
  fleeces: "sweater",
  jassen: "jacket",
  bodywarmers: "jacket",
  softshells: "jacket",
  broeken: "workwear",
  werkjassen: "workwear",
  werkbroeken: "workwear",
  veiligheidskleding: "workwear",
  schorten: "workwear",
  labjassen: "workwear",
  baby: "kids",
};


const SITE_URL = "https://www.magicprint.nl";

const ProductCategory = () => {
 const { slug } = useParams<{ slug: string }>();
 const category = getCategoryBySlug(slug || "");
 const [activeSubcategory, setActiveSubcategory] = useState<string | null>(null);

 useEffect(() => {
 if (category) {
 document.title = `${category.name} — Magic Print`;
 const meta = document.querySelector('meta[name="description"]');
 if (meta) meta.setAttribute("content", category.description);
 trackEvent("categorie_bekeken", {
 categorie: category.name,
 slug: category.slug,
 });
 }
 window.scrollTo(0, 0);
 }, [category]);

 const liveQuery = useMemo(() => {
 const s = slug || "";
 const sub = activeSubcategory || "";
 const cat = (sub && CATEGORY_BY_SUBSLUG[sub]) || CATEGORY_BY_SLUG[s] || "";
 const gender = GENDER_BY_SLUG[s] || "";
 const search = s === "organic" ? "organic" : sub && !CATEGORY_BY_SUBSLUG[sub] ? sub.replace(/-/g, " ") : "";
 return { category: cat, gender, search };
 }, [slug, activeSubcategory]);

 const { data: liveData, isLoading } = useToptexProducts({
 page: 1,
 pageSize: 24,
 category: liveQuery.category,
 gender: liveQuery.gender,
 search: liveQuery.search,
 enabled: !!category,
 });
 const liveProducts = liveData?.items ?? [];
 const liveTotal = liveData?.total_count ?? 0;

 if (!category) return <Navigate to="/producten" replace />;


 const pageTitle = `${category.name} bedrukken & borduren — Magic Print`;
 const pageDesc = category.description;
 const canonicalUrl = `${SITE_URL}/producten/${category.slug}`;
 const ogImg = `${SITE_URL}/og-image.jpg`;

 return (
 <>
 <Helmet>
 <title>{pageTitle}</title>
 <meta name="description" content={pageDesc} />
 <link rel="canonical" href={canonicalUrl} />
 <meta property="og:type" content="website" />
 <meta property="og:site_name" content="Magic Print" />
 <meta property="og:locale" content="nl_NL" />
 <meta property="og:title" content={pageTitle} />
 <meta property="og:description" content={pageDesc} />
 <meta property="og:url" content={canonicalUrl} />
 <meta property="og:image" content={ogImg} />
 <meta name="twitter:card" content="summary_large_image" />
 <meta name="twitter:title" content={pageTitle} />
 <meta name="twitter:description" content={pageDesc} />
 <meta name="twitter:image" content={ogImg} />
 </Helmet>
 <Navbar />
 <main className="pt-32 md:pt-40 pb-20 min-h-screen bg-background">
 <div className="container">
 {/* Breadcrumb */}
 <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
 <Link to="/producten" className="hover:text-primary transition-colors">Producten</Link>
 <span>/</span>
 <span className="text-foreground font-medium">{category.name}</span>
 </div>

 <div className="flex flex-col lg:flex-row gap-8">
 {/* Sidebar */}
 <aside className="lg:w-64 shrink-0">
 <div className="lg:sticky lg:top-24 space-y-4">
 <Link to="/producten" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
 <ArrowLeft className="w-4 h-4" /> Alle categorieën
 </Link>

 <div className="rounded-xl border border-border bg-card p-4">
 <h3 className="font-semibold mb-3 flex items-center gap-2 text-[#0a2933]">
 <Filter className="w-4 h-4" /> Subcategorieën
 </h3>
 <div className="flex flex-col gap-1">
 <button
 onClick={() => setActiveSubcategory(null)}
 className={`text-left text-sm px-3 py-2 rounded-lg transition-colors ${
 !activeSubcategory ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground"
 }`}
 >
 Alles{liveTotal > 0 && !activeSubcategory ? ` (${liveTotal})` : ""}
 </button>
 {category.subcategories.map((sub) => {
 return (
 <button
 key={sub.slug}
 onClick={() => setActiveSubcategory(sub.slug)}
 className={`text-left text-sm px-3 py-2 rounded-lg transition-colors ${
 activeSubcategory === sub.slug
 ? "bg-primary text-primary-foreground"
 : "hover:bg-muted text-muted-foreground"
 }`}
 >
 {sub.name}
 </button>
 );
 })}
 </div>
 </div>

 {/* Other categories */}
 <div className="rounded-xl border border-border bg-card p-4">
 <h3 className="font-semibold mb-3 text-[#0a2933]">Andere categorieën</h3>
 <div className="flex flex-col gap-1">
 {productCategories
 .filter((c) => c.id !== category.id)
 .map((c) => (
 <Link
 key={c.id}
 to={`/producten/${c.slug}`}
 className="text-sm px-3 py-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors flex items-center gap-2"
 >
 <span>{c.icon}</span> {c.name}
 </Link>
 ))}
 </div>
 </div>
 </div>
 </aside>

 {/* Main content */}
 <div className="flex-1">
 <div className="mb-8">
 <h1 className="text-3xl md:text-4xl font-bold mb-3">
 {category.icon} {category.name} bedrukken
 </h1>
 <p className="text-muted-foreground mb-2">{category.description}</p>
 <p className="text-sm text-muted-foreground">
 <strong>{category.productCount}+</strong> producten beschikbaar • Vanaf 10 stuks • Offerte op maat
 </p>
 </div>

 {isLoading ? (
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
 {Array.from({ length: 9 }).map((_, i) => (
 <div key={i} className="rounded-xl border border-border bg-card overflow-hidden">
 <div className="aspect-square bg-muted animate-pulse" />
 <div className="p-4 space-y-2">
 <div className="h-3 w-1/2 bg-muted animate-pulse rounded" />
 <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
 </div>
 </div>
 ))}
 </div>
 ) : (
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
 {liveProducts.map((product) => (
 <div
 key={product.ref}
 className="rounded-xl border border-border bg-card overflow-hidden hover:border-primary transition-all group"
 >
 <Link to={`/producten/detail/${encodeURIComponent(product.ref)}`} className="block aspect-square bg-white flex items-center justify-center">
 {product.imageBase ? (
 <img
 src={imageSrc(product.imageBase, 300)}
 srcSet={imageSrcSet(product.imageBase)}
 sizes="(max-width: 768px) 45vw, 300px"
 onError={imageFallback}
 alt={`${product.brand} ${product.name}`}
 className="w-full h-full object-contain p-4"
 loading="lazy"
 decoding="async"
 />
 ) : (
 <svg viewBox="0 0 64 64" className="w-14 h-14 text-muted-foreground/40" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
 <path d="M20 10 L26 6 L38 6 L44 10 L56 18 L48 26 L44 22 L44 56 L20 56 L20 22 L16 26 L8 18 Z" />
 </svg>
 )}
 </Link>
 <div className="p-4">
 <p className="text-xs text-muted-foreground mb-1">{product.brand} • {product.ref}</p>
 <h3 className="font-semibold text-sm mb-3 group-hover:text-primary transition-colors text-[#0a2933]">
 <Link to={`/producten/detail/${encodeURIComponent(product.ref)}`}>{product.name}</Link>
 </h3>
 <Button size="sm" className="w-full" asChild>
 <Link to={`/offerte-aanvragen?product=${encodeURIComponent(product.name)}&code=${encodeURIComponent(product.ref)}`}>
 Gratis offerte aanvragen
 </Link>
 </Button>
 </div>
 </div>
 ))}
 </div>
 )}

 {!isLoading && liveProducts.length === 0 && (
 <p className="text-center text-muted-foreground py-12">
 Geen producten gevonden in deze subcategorie.
 </p>
 )}

 {liveTotal > liveProducts.length && (
 <div className="text-center mb-10">
 <Button variant="outline" asChild>
 <Link to={`/producten?${new URLSearchParams({ ...(liveQuery.category ? { cat: liveQuery.category } : {}), ...(liveQuery.search ? { q: liveQuery.search } : {}) }).toString()}`}>
 Bekijk alle {liveTotal.toLocaleString("nl-NL")} producten <ArrowRight className="ml-2 w-4 h-4" />
 </Link>
 </Button>
 </div>
 )}


 <div className="p-6 rounded-2xl bg-primary/5 border border-primary/20 text-center">
 <h2 className="text-xl font-bold mb-2 text-[#0a2933]">Meer dan {category.productCount} producten beschikbaar</h2>
 <p className="text-muted-foreground mb-4">
 Dit is een selectie uit ons assortiment. Wij hebben nog veel meer {category.name.toLowerCase()} beschikbaar.
 Neem contact op voor het volledige aanbod en een offerte op maat.
 </p>
 <div className="flex flex-wrap justify-center gap-3">
 <Button asChild>
 <Link to="/offerte-aanvragen">
 Gratis offerte aanvragen <ArrowRight className="ml-2 w-4 h-4" />
 </Link>
 </Button>
 <Button variant="outline" asChild>
 <a href="https://wa.me/31652526002" target="_blank" rel="noopener noreferrer">
 WhatsApp ons
 </a>
 </Button>
 </div>
 </div>
 </div>
 </div>
 </div>
 </main>
 <Footer />
 <WhatsAppButton />
 </>
 );
};

export default ProductCategory;
